/**
 * QUAND proposer un compte — **logique pure** (ROADMAP 6.2).
 *
 * ══ La règle de spec qu'on ne contourne pas ══════════════════════════════════
 *
 * L'authentification est **toujours optionnelle** (§3), et le §1 promet un jeu « jouable
 * en moins de 5 s sans compte ». Un mur d'inscription à l'ouverture violerait les deux.
 * Décision déjà prise et à respecter : **l'e-mail se demande au moment du partage, en
 * invitation refusable.**
 *
 * ══ Trois moments, et pas un de plus ═════════════════════════════════════════
 *
 * Chacun est choisi parce que le joueur vient d'y gagner quelque chose qu'il a une
 * raison de vouloir garder — jamais pendant une frustration :
 *
 * · **après un partage** — il vient de donner son lien à quelqu'un ;
 * · **après une nouvelle ère** — il vient d'obtenir un bonus permanent ;
 * · **avant un achat de gemmes** — il s'apprête à payer, et un achat sans compte est ce
 *   qui se perd le plus mal.
 *
 * ⚠️ **L'argument est la SAUVEGARDE, jamais la newsletter.** « Ta ville te suit sur ton
 * téléphone » convertit ; « inscris-toi » non. Le consentement à des e-mails est une
 * case séparée, décochée (6.3) — les confondre serait à la fois inefficace et illégal.
 *
 * ══ Ce que ce module garantit ════════════════════════════════════════════════
 *
 * Un joueur relié n'est plus jamais sollicité. Un joueur qui refuse ne revoit pas la
 * même proposition avant une semaine. Et les trois moments ont chacun leur horloge : un
 * refus au partage n'empêche pas la proposition avant un achat, qui n'a pas le même
 * argument ni la même urgence.
 */

/** Les trois seuls instants où le jeu a le droit de proposer un compte. */
export type AccountMoment = 'shared' | 'era' | 'purchase';

export const ACCOUNT_MOMENTS: readonly AccountMoment[] = ['shared', 'era', 'purchase'];

/**
 * Délai avant de reproposer le MÊME moment.
 *
 * Une semaine : assez long pour qu'un refus soit respecté, assez court pour qu'un joueur
 * régulier revoie l'offre après avoir pris goût au jeu. Un rappel quotidien serait du
 * harcèlement, un rappel mensuel ne convertirait personne.
 */
export const ACCOUNT_PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export interface AccountPromptState {
  /** Le compte porte-t-il déjà un e-mail ? */
  readonly linked: boolean;
  /** Dernière proposition par moment, en horodatage absolu. */
  readonly lastPrompted: Readonly<Partial<Record<AccountMoment, number>>>;
}

/**
 * Peut-on proposer un compte à cet instant précis ?
 *
 * ⚠️ Rend `false` pour un joueur relié **avant toute autre considération** : lui
 * reproposer ce qu'il a déjà fait est le genre de détail qui fait douter du reste du jeu.
 */
export function shouldPromptAccount(
  moment: AccountMoment,
  state: AccountPromptState,
  now: number,
): boolean {
  if (state.linked) return false;

  const last = state.lastPrompted[moment];
  if (last === undefined) return true;

  /*
    Une horloge locale peut reculer — changement de fuseau, correction NTP, appareil
    remis à l'heure. Un « dernier rappel » situé dans le futur ne doit pas faire taire la
    proposition pour toujours : on la considère alors comme jamais faite.
  */
  if (last > now) return true;

  return now - last >= ACCOUNT_PROMPT_COOLDOWN_MS;
}

/** L'état après une proposition affichée — refusée ou non, elle a été vue. */
export function markPrompted(
  state: AccountPromptState,
  moment: AccountMoment,
  now: number,
): AccountPromptState {
  return { ...state, lastPrompted: { ...state.lastPrompted, [moment]: now } };
}
